/**
 * OAuth / OIDC claim utilities — relocated from the frontend into the SDK
 * as part of the auth cutover. Pure functions (no React, no DOM, no
 * vendor) — `src/auth/` core, ships to web AND React Native.
 *
 * `atob` is the only platform assumption: standard on web, polyfilled on
 * React Native (Hermes / RN base64). Used only to read the *unverified*
 * payload of an OIDC token for client-side display claims — the signature
 * is verified server-side before any sub-org / session is created.
 */

import type { OAuthProfile } from './auth-sync.js';

/** Profile data extracted from an OAuth provider's OIDC token. */
export interface ProviderProfile {
  provider: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  email: string | null;
}

/** Result of capturing an OAuth flow via onOauthSuccess. */
export interface OAuthCapturedResult {
  oidcToken: string;
  providerName: string;
  profile: ProviderProfile;
  providerUserId: string | null;
}

/** Providers that can be connected from the UI. */
export const CONNECTABLE_PROVIDERS: readonly string[] = ['x', 'google', 'apple'];

/**
 * Decode the payload of a JWT WITHOUT verifying the signature. Safe for
 * extracting OIDC profile claims client-side — Turnkey / the backend
 * verify the signature server-side.
 */
export function decodeJwtPayload(jwt: string): Record<string, unknown> {
  const parts = jwt.split('.');
  const payload = parts[1];
  if (!payload) return {};
  try {
    const bytes = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    // atob yields one char per byte, but the JSON is UTF-8: reassemble the
    // bytes so non-ASCII names (Cyrillic, CJK, emoji) survive. Percent-decoding
    // works on Hermes too, which has no guaranteed TextDecoder.
    const json = decodeURIComponent(
      Array.from(bytes, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    // SILENT-CATCH: malformed JWT payload is expected for non-OIDC tokens.
    return {};
  }
}

/** Normalize Turnkey's provider name to Morfi's convention (`twitter` → `x`). */
export function normalizeProviderName(providerName: string): string {
  return providerName === 'twitter' ? 'x' : providerName;
}

/** Extract a provider profile from a raw OIDC token (Google / X). */
export function extractProfileFromOidc(oidcToken: string, providerName: string): ProviderProfile {
  const claims = decodeJwtPayload(oidcToken);
  return {
    provider: normalizeProviderName(providerName),
    username: null,
    displayName: typeof claims.name === 'string' ? claims.name : null,
    avatarUrl: typeof claims.picture === 'string' ? claims.picture : null,
    email: typeof claims.email === 'string' ? claims.email : null,
  };
}

/** Extract the provider user ID (`sub` / Turnkey `user_id`) from an OIDC token. */
export function extractProviderUserId(oidcToken: string): string | null {
  const claims = decodeJwtPayload(oidcToken);
  if (typeof claims.sub === 'string') return claims.sub;
  if (typeof claims.user_id === 'string') return claims.user_id;
  return null;
}

/**
 * Whether a Google email may connect to this account. If the account
 * already has an email, the Google email must match. Returns `{allowed}`.
 */
export function validateGoogleEmailMatch(
  accountEmail: string | null | undefined,
  googleEmail: string | null,
): { allowed: true } | { allowed: false; accountEmail: string } {
  if (!accountEmail || accountEmail === '' || !googleEmail) {
    return { allowed: true };
  }
  if (googleEmail.toLowerCase() === accountEmail.toLowerCase()) {
    return { allowed: true };
  }
  return { allowed: false, accountEmail };
}

/**
 * Detect the OAuth provider from OIDC token claims.
 * - Google: `iss` contains `accounts.google`
 * - Apple: `iss` contains `apple`
 * - X/Twitter: `iss` contains `oidc.turnkey.com` with a numeric `sub`
 */
export function detectOAuthProvider(oidcToken: string): {
  provider: string | null;
  xUserId: string | null;
} {
  const claims = decodeJwtPayload(oidcToken);
  const iss = typeof claims.iss === 'string' ? claims.iss : '';
  const sub = typeof claims.sub === 'string' ? claims.sub : '';

  if (iss.includes('google') || iss.includes('accounts.google')) {
    return { provider: 'google', xUserId: null };
  }
  if (iss.includes('apple')) {
    return { provider: 'apple', xUserId: null };
  }
  if (iss.includes('oidc.turnkey.com') && /^\d+$/.test(sub)) {
    return { provider: 'x', xUserId: sub };
  }
  return { provider: null, xUserId: null };
}

/** The OAuth profile the session mint sends, read from the OIDC id_token's claims. */
export function oauthProfileFromIdToken(idToken: string): OAuthProfile {
  const claims = decodeJwtPayload(idToken);
  const detected = detectOAuthProvider(idToken);
  return {
    name: typeof claims.name === 'string' ? claims.name : null,
    picture: typeof claims.picture === 'string' ? claims.picture : null,
    email: typeof claims.email === 'string' ? claims.email : null,
    provider: detected.provider,
    providerUserId: typeof claims.sub === 'string' ? claims.sub : null,
    xUserId: detected.xUserId,
    oidcToken: idToken,
  };
}

export interface TurnkeyOauthProvider {
  providerId: string;
  providerName: string;
  /** OIDC subject Turnkey recorded for the identity; absent on older records. */
  subject?: string;
}

/**
 * X answers to two Turnkey names: legacy login-created providers are named
 * `twitter`, while the connect flow registers them as `x` (the providerName we
 * pass to `addOauthProvider`). Matching only one leaves a dangling provider on
 * the sub-org after disconnect, which then rejects every reconnect as a
 * duplicate identity.
 */
function turnkeyNamesFor(morfiProvider: string): readonly string[] {
  return morfiProvider === 'x' ? ['x', 'twitter'] : [morfiProvider];
}

/**
 * Every Turnkey OAuth provider bound to a Morfi provider. One Morfi provider
 * can map to several: Apple registers one per audience (iOS bundle id and web
 * Services ID), and X has its two names.
 */
export function findTurnkeyProviders(
  oauthProviders: readonly TurnkeyOauthProvider[],
  morfiProvider: string,
): TurnkeyOauthProvider[] {
  const tkNames = new Set(turnkeyNamesFor(morfiProvider));
  return oauthProviders.filter((p) => tkNames.has(p.providerName.toLowerCase()));
}

/**
 * Whether the sub-org already holds the exact identity carried by `oidcToken`.
 *
 * A disconnect that removed the Morfi row but left the Turnkey provider
 * behind makes `addOauthProvider` reject that identity as a duplicate, so the
 * connect flow can never repair the row. When the sub-org already holds this
 * subject the caller skips the Turnkey step and only re-links on the backend,
 * which verifies the token itself. A different subject, or a record without
 * one, never matches, so a second X account still goes through Turnkey.
 */
export function turnkeyAlreadyHoldsIdentity(
  oauthProviders: readonly TurnkeyOauthProvider[],
  morfiProvider: string,
  oidcToken: string,
): boolean {
  const subject = extractProviderUserId(oidcToken);
  if (subject === null) return false;
  const tkNames = turnkeyNamesFor(morfiProvider);
  return oauthProviders.some(
    (p) => tkNames.includes(p.providerName.toLowerCase()) && p.subject === subject,
  );
}
